import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Users, HeartPulse, Mail, ArrowLeft } from "lucide-react";
import { LEGAL_EFFECTIVE_DATE, LEGAL_VERSION, SUPPORT_EMAIL } from "../lib/legal";
import { useSettings } from "../context/SettingsContext";

const TEAL = "#0D9488";

/**
 * /privacy — Comprehensive privacy policy tailored to The Amino Exchange.
 * Written to be readable by a human, not just a lawyer: short paragraphs,
 * plain language, and iconography flagging the four sensitive-topic sections
 * the product owner called out (data storage, coaching sharing, medical
 * disclaimer, and user data rights).
 */
function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5" style={{ color: TEAL }} />}
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-3 text-[15px] leading-relaxed text-slate-700">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  const { settings } = useSettings();
  const site = settings?.site_name || "The Amino Exchange";
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold text-slate-900 hover:text-teal-700 flex items-center gap-2" data-testid="privacy-back-home">
            <ArrowLeft className="w-4 h-4" /> {site}
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Legal · v{LEGAL_VERSION}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-page">
        <div className="mb-8">
          <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Legal</div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 text-sm">Effective {LEGAL_EFFECTIVE_DATE} · Last updated {LEGAL_EFFECTIVE_DATE}</p>
        </div>

        <div className="mb-8 rounded-lg border border-teal-200 bg-teal-50/50 p-4 text-sm text-teal-900" data-testid="privacy-intro">
          <b>Plain-English summary.</b> {site} is a private wellness journal.
          Your peptide doses, meals, weigh-ins, and messages belong to you.
          We never sell your data. We only share information with a coach
          when <em>you</em> explicitly connect with one, and we make it easy
          to export or delete everything you've logged.
        </div>

        <Section id="scope" icon={ShieldCheck} title="1. Who this policy covers">
          <p>
            This Privacy Policy explains how <b>{site}</b> ("we", "us", the "Service") collects, uses,
            stores, and shares information when you use our website and mobile-web
            application. It applies to visitors, registered users, and coaches on the Service.
          </p>
          <p>
            By creating an account, using the Service, or clicking "I agree to the Privacy Policy
            and Terms of Service" during signup, you consent to the practices described here.
          </p>
        </Section>

        <Section id="what-we-collect" icon={Lock} title="2. What we collect">
          <p><b>Account information.</b> Your name, email address, hashed password, and (optionally) a
            handle, avatar URL, timezone, and short bio.</p>
          <p><b>Health & dosage data.</b> Everything you log inside the app: peptide names, doses,
            injection sites and times, vial inventory, reconstitution notes, side effects, side-effect
            journal tags, cycle definitions, body weight, waist and body-fat measurements, goal
            weights, calorie/macro goals, nutrition entries (including AI meal parses, voice-to-text
            transcripts, and photo thumbnails you choose to save), supplement logs, and any notes.
          </p>
          <p><b>Communication.</b> Messages you send to coaches, community posts and replies, direct
            messages, testimonials, and reports.</p>
          <p><b>Technical data.</b> IP address, browser user agent, feature-flag toggles, session
            tokens (stored as JWTs on your device), timezone, and the last time you logged in. We use
            this to keep your session secure and to keep the app running smoothly.</p>
          <p><b>Payments (if applicable).</b> If you upgrade to Pro, our payment providers
            (Stripe, PayPal, or Venmo) collect and process your billing information. We only receive
            confirmation events (subscription started, renewed, cancelled) via signed webhooks —
            <b> we never store or see your full card number.</b>
          </p>
          <p><b>What we do <em>not</em> collect.</b> We do not ask for social security numbers,
            insurance details, government ID, or any information beyond what you voluntarily enter.
            We do not track you across other websites.</p>
        </Section>

        <Section id="how-we-store" icon={Lock} title="3. How we securely store your health & dosage data">
          <p>
            Your data is stored in <b>MongoDB</b>, a document database hosted in a secured cloud
            environment. Each record is written into a per-collection document keyed to your unique
            user ID so your data is logically isolated from every other user.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Encryption in transit.</b> Every request between your browser and our servers
              uses TLS 1.2+ (HTTPS). Signed JWT session tokens gate all API endpoints; anonymous
              access to your data is refused at the database layer.</li>
            <li><b>Encryption at rest.</b> The underlying MongoDB volumes are encrypted at the disk
              layer by our cloud provider. Password credentials are stored only as bcrypt hashes —
              plaintext passwords are never persisted, and no employee can recover a password from
              our systems.</li>
            <li><b>Access control.</b> Only a small number of production engineers can access the
              database, and access is logged. Administrators of the Service can view a user's
              account status and role for support purposes but cannot see logged doses or messages
              without explicit permission.</li>
            <li><b>Backups.</b> We keep encrypted rolling backups so we can restore your account in
              the event of infrastructure failure. Backups are pruned on a rotating schedule.</li>
            <li><b>Signed webhooks.</b> Payment webhooks from Stripe and PayPal are verified with
              HMAC-SHA256 signatures before any change is applied to your account.</li>
          </ul>
          <p>
            No system is 100% impregnable. If we ever discover a data incident that materially
            affects your account, we will notify you by email without undue delay.
          </p>
        </Section>

        <Section id="how-we-use" icon={ShieldCheck} title="4. How we use your data">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To operate the Service — save your logs, compute your countdowns, generate charts,
              build your history exports, and power your dashboard.</li>
            <li>To send you the reminder and digest emails you opt into (dose reminders, weekly
              digests, weight streaks). You can unsubscribe from every recurring email.</li>
            <li>To improve accuracy of our AI features (voice-to-nutrition, meal photo parsing,
              text meal parsing). AI providers we use — currently OpenAI Whisper and Anthropic
              Claude — process your submission to return macros. Providers do not retain your
              content beyond the transient request per their zero-retention APIs.</li>
            <li>To protect the Service — detect fraud, ban abusive accounts, and keep the community
              safe.</li>
            <li>To comply with law.</li>
          </ul>
          <p>We do <b>not</b> sell your personal data. We do <b>not</b> serve third-party
            advertising. Your health data is never packaged for marketers.</p>
        </Section>

        <Section id="coaching-sharing" icon={Users} title="5. Coaching: what a coach can and cannot see">
          <p>
            {site} includes an optional <b>Coaching</b> feature. When you connect to a coach — by
            accepting their invite or requesting their guidance — you are <em>affirmatively</em>
            granting that coach access to your logged data for the duration of the coaching
            relationship.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>What a coach sees:</b> your active peptide cycles, shots, doses, injection sites,
              weight logs, nutrition entries (including AI-parsed meals), streak progress, and any
              messages you send them.</li>
            <li><b>What a coach does NOT see:</b> your password, your billing information, private
              community DMs with users other than themselves, or accounts you have not connected
              to them.</li>
            <li><b>Ending a coaching relationship.</b> You can disconnect from a coach at any time
              from your Profile. Once disconnected, the coach loses access to any future logs. Data
              they already saw remains in their memory but is no longer accessible from their
              dashboard.</li>
            <li><b>Coach obligations.</b> Coaches on our platform agree to keep client data
              confidential, use it only to provide guidance, and never re-share it. Violations are
              grounds for immediate removal from the Service.</li>
            <li><b>Admin oversight.</b> Site administrators can, in limited circumstances (a
              user-reported dispute or safety concern), review a coach's client-visible data. Any
              such access is logged.</li>
          </ul>
        </Section>

        <Section id="ai-third-parties" icon={ShieldCheck} title="6. AI providers & other third parties">
          <p>The Service relies on a small number of well-known infrastructure partners:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>OpenAI</b> (Whisper) — transcribes your voice recordings into meal text.</li>
            <li><b>Anthropic</b> (Claude) — parses meal text and photos into estimated macros.</li>
            <li><b>Resend / SMTP</b> — delivers transactional and reminder emails you opt into.</li>
            <li><b>Stripe / PayPal / Venmo</b> — process subscription payments (if you upgrade).</li>
            <li><b>Cloud hosting provider</b> — runs our servers and MongoDB.</li>
          </ul>
          <p>Each provider is contractually obligated to use your data only to deliver its service
            back to us. None of them are permitted to use your data to train general-purpose
            models.</p>
        </Section>

        <Section id="moderation" icon={ShieldCheck} title="7. Community moderation & reported content">
          <p>
            Privacy is a core value — but so is a safe community. When any member uses the
            in-app <b>Report</b> button on a post, reply, or direct message, that report is
            reviewed by <b>{site}</b> staff (admins and moderators).
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Staff review rights.</b> By using the community and messaging features you
              acknowledge that {site} staff has the right to <b>review and remove</b> any
              content that has been reported for violating our safety rules — including
              harassment, spam, doxxing, illegal content, and misinformation.</li>
            <li><b>What we log.</b> Every report stores the flagged content (as a short snippet),
              the author of the content, who reported it, the reason, and any admin action
              taken. This log lives in a separate collection accessible only to admin +
              moderator accounts.</li>
            <li><b>Purpose limitation.</b> These moderation logs are stored and reviewed
              strictly for the purpose of community safety — never used for marketing, sold to
              third parties, or exposed to any user outside the reporter and staff who need to
              action the report.</li>
            <li><b>Repeat offenders.</b> If a member accumulates three reports in a rolling
              seven-day window that end in a Remove action, their posting privileges are
              temporarily paused for 24 hours ("<b>auto-mute</b>") and admins are notified.</li>
            <li><b>Data subject rights still apply.</b> You can request a copy or deletion of
              your own moderation history — same rights as the rest of your data (§10).</li>
          </ul>
        </Section>

        <Section id="disclaimer" icon={HeartPulse} title="8. Medical disclaimer">
          <p>
            <b>{site} is a tracking tool. It is not a doctor, a pharmacist, or a substitute for
            professional medical advice.</b>
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Nothing you see in the peptide library, calculator, community, or AI outputs is a
              prescription, a diagnosis, or a recommendation of treatment.</li>
            <li>Peptides, hormones, and supplements can carry serious health risks. You should
              consult a licensed healthcare provider before starting, changing, or stopping any
              regimen, and especially before self-administering injectable compounds.</li>
            <li>AI-generated nutrition estimates are approximations. Do not rely on them for
              medical, allergen, or clinical purposes.</li>
            <li>The countdowns, reminders, and calculators are convenience tools. You are solely
              responsible for what you do with the numbers you log.</li>
            <li>If you are pregnant, breastfeeding, under 18, or managing a medical condition, seek
              qualified professional care rather than relying on this Service.</li>
          </ul>
          <p><b>If you believe you are having a medical emergency, call your local emergency
            number immediately.</b></p>
        </Section>

        <Section id="your-rights" icon={Users} title="9. Your rights: access, correction, deletion">
          <p>You have the following rights over the data you've entrusted to us:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Access & export.</b> You can view every entry inside the app and download
              your dosage history, nutrition log, and combined health report as PDF or CSV at any
              time from the History and Nutrition pages.</li>
            <li><b>Correction.</b> You can edit your name, handle, timezone, avatar, and every
              logged shot, meal, weight, or vial directly in the app. If you spot something we
              can't edit ourselves, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline">{SUPPORT_EMAIL}</a>.</li>
            <li><b>Deletion.</b> You can delete individual entries with a 10-second Undo safety net.
              To delete your entire account and every associated record, email
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline"> {SUPPORT_EMAIL}</a> from
              the address on file and we will honor the request within 30 days.</li>
            <li><b>Withdraw consent.</b> You can turn off individual features (AI parsing,
              coaching, community, marketing emails) at any time from your Profile or unsubscribe
              links.</li>
            <li><b>Data portability.</b> Our CSV exports are open-format and can be imported into
              any spreadsheet or health-tracking tool.</li>
          </ul>
          <p>
            Depending on where you live, you may also have rights under GDPR (EU/UK), the CCPA/CPRA
            (California), or other privacy laws — including the right to object, restrict
            processing, or file a complaint with a regulator. Contact us and we will respond in
            accordance with the applicable law.
          </p>
        </Section>

        <Section id="retention" icon={Lock} title="10. Data retention">
          <p>We keep your account data for as long as your account is active. If you delete your
            account we permanently delete your logs from our primary database within 30 days.
            Encrypted backups roll off within an additional 90 days. Aggregated, non-personal
            statistics (e.g. "500 users this month") may be retained indefinitely.</p>
        </Section>

        <Section id="children" icon={ShieldCheck} title="11. Age requirement">
          <p>The Service is intended for adults <b>21 and older</b> — signup requires an
            age attestation, and we do not knowingly collect data from anyone under 21. If you
            believe someone under 21 has created an account, please email us and we will delete it.</p>
        </Section>

        <Section id="changes" icon={ShieldCheck} title="12. Changes to this policy">
          <p>We may update this policy from time to time. When we do, we'll change the "Last
            updated" date at the top and — for material changes — email you or ask you to
            re-accept the updated terms next time you log in.</p>
        </Section>

        <Section id="contact" icon={Mail} title="13. Contact us">
          <p>Privacy questions or data requests? Reach us at
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline font-semibold"> {SUPPORT_EMAIL}</a>. We aim to respond within 5 business days.</p>
        </Section>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 border-t border-slate-200 pt-6">
          <Link to="/terms" className="text-teal-700 hover:underline font-semibold" data-testid="privacy-to-terms">
            Read the Terms of Service →
          </Link>
          <Link to="/" className="hover:underline" data-testid="privacy-home">Back to {site}</Link>
        </div>
      </main>
    </div>
  );
}
